import {
    keepPreviousData,
    useInfiniteQuery,
    useQueries,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { ApiError } from "@jobenvoy/core";
import { useToast } from "@jobenvoy/ui";
import type { SearchPostingsQuery } from "@/lib/api/catalogue";
import { describeError } from "@/lib/api/client";
import { track } from "@/lib/analytics";
import {
    bestFit,
    fitPostings,
    getBaseline,
    getDemographics,
    getExport,
    getUploadRead,
    listExports,
    mostKeywords,
    previewPdf,
    readUpload,
    requestExport,
    saveBaseline,
    saveDemographics,
    uploadResumeFile,
    withdrawEeoConsent,
    type Demographics,
    type EeoConsent,
    type ImportDetails,
    type Resume,
    type ResumeFormat,
    type ResumeVersion,
    type SaveResumeRequest,
    getResumeVersion,
    getTailoredChanges,
    getTailoredLink,
    MAX_FIT_POSTINGS,
    type PostingFit,
    type TailoredResumeLink,
} from "@/lib/api/resumeService";
import { pointTab } from "@/lib/leave";
import { batches, mergeBatches } from "@/lib/batches";
import { withSkills } from "@/lib/posting/tailorChoices";
import { keys } from "./keys";
import { ONE_AT_A_TIME, useSingleFlightMutation } from "./singleFlight";

/**
 * Opens a tailored resume's PDF in a new tab. The link is signed and
 * short lived, so it is asked for at the press, not ahead of it. The tab
 * is opened first, in the press itself, so a popup blocker lets it
 * through, and pointed at the PDF once the link is back.
 */
export function useOpenTailored() {
    const toast = useToast();
    return useSingleFlightMutation({
        mutationFn: async (resumeId: string) => {
            // Not "noopener" here: with it the browser hands back no tab to
            // point. The opener is cut by hand instead.
            const tab = window.open("about:blank", "_blank");
            if (tab) tab.opener = null;
            try {
                const link = await getTailoredLink(resumeId);
                pointTab(tab, link.url);
            } catch (error) {
                tab?.close();
                throw error;
            }
        },
        onError: (error) => toast.error("The PDF could not be opened", describeError(error)),
    });
}

/**
 * The current resume's fit against each of these postings, keyed by
 * posting. Absent while it is asked; null when there is no resume to
 * score (the service answers 404).
 */
export function usePostingFits(postingIds: readonly string[]) {
    return useQueries({
        queries: batches(postingIds, MAX_FIT_POSTINGS).map((ids) => ({
            queryKey: keys.resume.fits(ids),
            queryFn: async (): Promise<Record<string, PostingFit> | null> => {
                try {
                    const list = await fitPostings(ids);
                    return Object.fromEntries(list.fits.map((fit) => [fit.posting_id, fit]));
                } catch (error) {
                    if (error instanceof ApiError && error.status === 404) return null;
                    throw error;
                }
            },
            staleTime: 60_000,
        })),
        combine: mergeBatches,
    });
}

/** A search's newest matches, the best fit first, `limit` of them. */
export function useBestFit(query: SearchPostingsQuery, limit: number, enabled = true) {
    return useQuery({
        queryKey: keys.resume.bestFit({ ...query, limit }),
        queryFn: () => bestFit({ ...query, limit }),
        enabled,
        placeholderData: keepPreviousData,
    });
}

/**
 * One page of every match of a search, the most keywords covered first.
 * Null when there is no resume to rank by. Not asked twice: a refusal
 * says the catalogue is busy, and the page shows the search in its order.
 */
export function useMostKeywords(
    query: SearchPostingsQuery,
    cursor: string | undefined,
    enabled: boolean,
) {
    return useQuery({
        queryKey: keys.resume.mostKeywords({ ...query, next_cursor: cursor }),
        queryFn: async () => {
            try {
                return await mostKeywords(query, cursor);
            } catch (error) {
                if (error instanceof ApiError && error.status === 404) return null;
                throw error;
            }
        },
        enabled,
        retry: false,
        staleTime: 60_000,
        placeholderData: keepPreviousData,
    });
}

/**
 * The same ranking a page at a time, each added to the last. Its first
 * page is null when there is no resume to rank by.
 */
export function useMostKeywordsPages(query: SearchPostingsQuery, enabled: boolean) {
    return useInfiniteQuery({
        queryKey: [...keys.resume.mostKeywords(query), "pages"],
        queryFn: async ({ pageParam }) => {
            try {
                return await mostKeywords(query, pageParam);
            } catch (error) {
                if (error instanceof ApiError && error.status === 404) return null;
                throw error;
            }
        },
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (page) => page?.next_cursor ?? undefined,
        enabled,
        retry: false,
        staleTime: 60_000,
    });
}

/**
 * A link's hash and size are what a row prints. The link itself lives
 * five minutes, so a read is kept four; opening the PDF asks afresh.
 */
const LINK_FRESH_MS = 4 * 60_000;

/** One tailored resume's hash and size, by its id. */
export function useTailoredLink(resumeId: string | null | undefined) {
    return useQuery({
        queryKey: keys.resume.tailored(resumeId ?? null),
        queryFn: () => getTailoredLink(resumeId as string),
        enabled: Boolean(resumeId),
        staleTime: LINK_FRESH_MS,
    });
}

/** Several tailored resumes' hashes and sizes, keyed by id; a missing one is absent. */
export function useTailoredLinks(resumeIds: readonly string[]): Record<string, TailoredResumeLink> {
    const unique = [...new Set(resumeIds)];
    const results = useQueries({
        queries: unique.map((resumeId) => ({
            queryKey: keys.resume.tailored(resumeId),
            queryFn: () => getTailoredLink(resumeId),
            staleTime: LINK_FRESH_MS,
        })),
    });
    return Object.fromEntries(
        results.flatMap((result, index) =>
            result.data ? [[unique[index] as string, result.data] as const] : [],
        ),
    );
}

/** What a tailored resume changed against the version it was made from. */
export function useTailoredChanges(resumeId: string | null | undefined) {
    return useQuery({
        queryKey: keys.resume.changes(resumeId ?? ""),
        queryFn: () => getTailoredChanges(resumeId as string),
        enabled: Boolean(resumeId),
        staleTime: Infinity,
    });
}

/** One saved version of the person's resume. Versions never change. */
export function useResumeVersion(version: number | null | undefined) {
    return useQuery({
        queryKey: keys.resume.version(version ?? 0),
        queryFn: () => getResumeVersion(version as number),
        enabled: typeof version === "number" && version > 0,
        staleTime: Infinity,
    });
}

/** A read that answers 404 as null: nothing there yet is an answer, not a failure. */
async function orNull<T>(read: () => Promise<T>): Promise<T | null> {
    try {
        return await read();
    } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
    }
}

/** The person's resume as it stands; null until they have saved one. */
export function useBaseline(enabled = true) {
    return useQuery({
        queryKey: keys.resume.baseline,
        queryFn: () => orNull(getBaseline),
        enabled,
    });
}

/**
 * The same read, run by the caller with `refetch`, for a press that must
 * hear back now. Offline or refused it fails at once: nothing pauses it
 * until the connection returns, and nothing tries it again.
 */
export function useBaselineNow() {
    return useQuery({
        queryKey: keys.resume.baseline,
        queryFn: () => orNull(getBaseline),
        enabled: false,
        networkMode: "always",
        retry: false,
    });
}

/** What one upload was read as; null when it has not been. A read is kept and never changes. */
export function useUploadRead(uploadId: string | null | undefined) {
    return useQuery({
        queryKey: keys.resume.read(uploadId ?? ""),
        queryFn: () => orNull(() => getUploadRead(uploadId as string)),
        enabled: Boolean(uploadId),
        staleTime: Infinity,
    });
}

/**
 * Saves a new version. Every resume read (the fit, best fit, the version)
 * is fetched again, since each is scored against the version that stands.
 */
export function useSaveBaseline() {
    const client = useQueryClient();
    const toast = useToast();
    return useSingleFlightMutation(
        {
            mutationFn: (request: SaveResumeRequest) => saveBaseline(request),
            onSuccess: (version: ResumeVersion) => {
                client.setQueryData(keys.resume.baseline, version);
                void client.invalidateQueries({ queryKey: keys.resume.all });
                track({ name: "resume_saved" });
                toast.success(`Saved as version ${version.version}`);
            },
            onError: (error) => toast.error("The resume was not saved", describeError(error)),
        },
        ONE_AT_A_TIME,
    );
}

/**
 * Adds skills the person says they have to their resume, as a new version.
 * It reads the version that stands just before, so nothing saved elsewhere
 * since is lost. No toast on success: the tailor that follows says enough.
 */
export function useAddSkills() {
    const client = useQueryClient();
    const toast = useToast();
    return useSingleFlightMutation(
        {
            mutationFn: async (skills: readonly string[]) => {
                const standing = await getBaseline();
                return saveBaseline({
                    resume: {
                        ...standing.resume,
                        skills: withSkills(standing.resume.skills, skills),
                    },
                    format: standing.format,
                });
            },
            onSuccess: (version: ResumeVersion) => {
                client.setQueryData(keys.resume.baseline, version);
                void client.invalidateQueries({ queryKey: keys.resume.all });
                track({ name: "resume_saved" });
            },
            onError: (error) =>
                toast.error(
                    "Your resume was not saved, so no tailor was spent",
                    describeError(error),
                ),
        },
        ONE_AT_A_TIME,
    );
}

/** Sends a file, or pasted text, to import from. The caller handles a refusal: it may be the guard. */
export function useUploadFile() {
    return useSingleFlightMutation(
        {
            mutationFn: ({ file, filename }: { file: Blob; filename: string | null }) =>
                uploadResumeFile(file, filename),
            onSuccess: () => track({ name: "resume_uploaded" }),
        },
        ONE_AT_A_TIME,
    );
}

/** Has an upload read into a draft. Spends one import, so billing's usage is fetched again. */
export function useReadUpload() {
    const client = useQueryClient();
    return useSingleFlightMutation(
        {
            mutationFn: ({ uploadId, details }: { uploadId: string; details: ImportDetails }) =>
                readUpload(uploadId, details),
            onSuccess: (read) => {
                client.setQueryData(keys.resume.read(read.upload_id), read);
                void client.invalidateQueries({ queryKey: ["billing"] });
            },
        },
        ONE_AT_A_TIME,
    );
}

/** Hands the browser a file to save. */
export function saveFile(blob: Blob, name: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = name;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

/** The draft on screen, set as the PDF the service would send, saved as resume.pdf. */
export function useDownloadPdf() {
    const toast = useToast();
    return useSingleFlightMutation(
        {
            mutationFn: async ({ resume, format }: { resume: Resume; format: ResumeFormat }) =>
                saveFile(await previewPdf(resume, format), "resume.pdf"),
            onError: (error) => toast.error("The PDF could not be made", describeError(error)),
        },
        ONE_AT_A_TIME,
    );
}

/**
 * The person's protected answers. Never retried in the background and
 * never sent to analytics: they are read when the page opens, and saved
 * when the person says so.
 */
export function useDemographics() {
    return useQuery({
        queryKey: keys.resume.demographics,
        queryFn: getDemographics,
        refetchOnWindowFocus: false,
    });
}

export function useSaveDemographics() {
    const client = useQueryClient();
    const toast = useToast();
    return useSingleFlightMutation(
        {
            mutationFn: ({ answers, consent }: { answers: Demographics; consent?: EeoConsent }) =>
                saveDemographics(answers, consent),
            onSuccess: (saved) => {
                client.setQueryData(keys.resume.demographics, saved);
                toast.success("Answers saved");
            },
            onError: (error) => {
                toast.error("The answers were not saved", describeError(error));
                // A refused consent may be for a statement since replaced:
                // read the answers again, and with them the statement in force.
                if (
                    error instanceof ApiError &&
                    error.status === 400 &&
                    error.body.replace(/^"/, "").startsWith("consent")
                )
                    void client.invalidateQueries({ queryKey: keys.resume.demographics });
            },
        },
        ONE_AT_A_TIME,
    );
}

/**
 * Withdraws the consent to keep self-identification answers: the resume
 * service resets each to "I choose not to disclose" and answers what it
 * now keeps.
 */
export function useWithdrawEeoConsent() {
    const client = useQueryClient();
    const toast = useToast();
    return useSingleFlightMutation(
        {
            mutationFn: () => withdrawEeoConsent(),
            onSuccess: (saved) => {
                client.setQueryData(keys.resume.demographics, saved);
                toast.success("Consent withdrawn", "Every self-identification answer is reset.");
            },
            onError: (error) => toast.error("Consent not withdrawn", describeError(error)),
        },
        ONE_AT_A_TIME,
    );
}

/** The person's data exports, the newest first; polled while one is being made. */
export function useExports() {
    return useQuery({
        queryKey: keys.resume.exports,
        queryFn: listExports,
        refetchInterval: (query) =>
            query.state.data?.exports.some(
                (entry) => entry.status === "PENDING" || entry.status === "BUILDING",
            )
                ? 15_000
                : false,
    });
}

/** Asks for an export. Refused while one is made, and for a day after. */
export function useRequestExport() {
    const client = useQueryClient();
    const toast = useToast();
    return useSingleFlightMutation(
        {
            mutationFn: requestExport,
            onSuccess: () => {
                track({ name: "export_requested" });
                toast.success(
                    "Export asked for",
                    "We email you when it is ready, usually in minutes.",
                );
                return client.invalidateQueries({ queryKey: keys.resume.exports });
            },
            onError: (error) => toast.error("No export started", describeError(error)),
        },
        ONE_AT_A_TIME,
    );
}

/**
 * Opens a ready export's zip. Its link lasts five minutes, so it is
 * asked for at the press, the tab opened first as useOpenTailored does.
 */
export function useDownloadExport() {
    const toast = useToast();
    return useSingleFlightMutation({
        mutationFn: async (exportId: string) => {
            const tab = window.open("about:blank", "_blank");
            if (tab) tab.opener = null;
            try {
                const found = await getExport(exportId);
                if (!found.download) throw new Error("The export is not ready to download.");
                pointTab(tab, found.download.url);
            } catch (error) {
                tab?.close();
                throw error;
            }
        },
        onError: (error) => toast.error("The export could not be opened", describeError(error)),
    });
}
